import { LitElement, css, html, nothing, svg } from 'lit';
import {
  DIMENSION_LABELS,
  formatMapLink,
  type Dimension,
  type MapLink,
  type PublicLandmark,
  type PublicMap,
  type ReleaseManifest,
} from '@ka/contracts';
import { shared } from '@ka/ui';
import { fmtDate, fmtTime } from './pages.ts';
import { navigate } from './router.ts';

type Adapter = import('@ka/map-adapter').MapAdapter;

interface MapChoice {
  label: string;
  episodeNumber: number;
  map: PublicMap;
}

/**
 * Map page. The 3D viewer is optional: the landmark list and static overview always work,
 * the viewer loads only on the map page, and on phones only after an explicit tap.
 */
export class KaMapView extends LitElement {
  static override properties = {
    manifest: { attribute: false },
    link: { attribute: false },
    query: { state: true },
    viewer: { state: true },
    places: { state: true },
    fs: { state: true },
  };
  declare manifest: ReleaseManifest;
  declare link: MapLink;
  declare query: string;
  declare viewer: 'idle' | 'loading' | 'ready' | 'failed' | 'unsupported';
  /** The landmark list drawn over the map is open. */
  declare places: boolean;
  /**
   * Full screen: 'native' uses the Fullscreen API; 'pseudo' makes the map cover the window
   * itself, for browsers without it on elements (iPhone Safari) or when the request is refused.
   */
  declare fs: 'off' | 'native' | 'pseudo';

  private adapter?: Adapter;
  /** Camera for looking at a landmark (set once the adapter module has loaded). */
  private viewOf: (l: PublicLandmark) => import('@ka/map-adapter').Camera = (l) => ({
    dimension: l.dimension,
    x: l.x,
    y: l.y,
    z: l.z,
  });
  private loadedKey = '';

  static override styles = [
    shared,
    css`
      .bar {
        display: flex;
        flex-wrap: wrap;
        gap: 0.75rem;
        align-items: end;
        margin-bottom: 1rem;
      }
      label {
        display: grid;
        gap: 0.25rem;
        font-size: 0.9rem;
      }
      select,
      input {
        min-height: 44px;
        font: inherit;
        background: var(--ka-stone-900);
        color: var(--ka-parchment);
        border: 2px solid var(--ka-stone-400);
        padding: 0 0.6rem;
      }
      .layout {
        display: grid;
        gap: 1rem;
      }
      .stage {
        position: relative;
        aspect-ratio: 4 / 3;
        background: #0b0c0e;
        border: 2px solid var(--ka-edge);
        box-shadow: var(--ka-bevel);
        display: grid;
        place-items: center;
      }
      .stage:fullscreen {
        aspect-ratio: auto;
      }
      .stage:-webkit-full-screen {
        aspect-ratio: auto;
        width: 100%;
        height: 100%;
      }
      /* Our own full screen where the browser has none for elements. */
      .stage.pseudo {
        position: fixed;
        inset: 0;
        z-index: 1000;
        aspect-ratio: auto;
        width: 100vw;
        height: 100vh;
        height: 100dvh;
        border: 0;
      }
      /* Map controls drawn over the 3D view (they stay with it in full screen). */
      .tools {
        position: absolute;
        top: 8px;
        left: 8px;
        right: 8px;
        z-index: 2;
        display: flex;
        justify-content: space-between;
        gap: 0.5rem;
        pointer-events: none;
      }
      .tools .btn {
        pointer-events: auto;
      }
      .places {
        position: absolute;
        top: calc(8px + 44px + 0.5rem + 8px);
        left: 8px;
        bottom: 8px;
        z-index: 2;
        width: min(20rem, calc(100% - 16px));
        box-sizing: border-box;
        display: grid;
        grid-template-rows: auto auto 1fr;
        gap: 0.5rem;
        padding: 0.75rem;
        overflow: hidden;
      }
      .places h2 {
        margin: 0;
        font-size: 1.1rem;
      }
      .places label {
        font-size: 0.85rem;
      }
      .places input {
        width: 100%;
        box-sizing: border-box;
      }
      .viewer {
        position: absolute;
        inset: 0;
      }
      .overlay {
        text-align: center;
        padding: 1rem;
      }
      .overview {
        width: 100%;
        height: 100%;
      }
      .overview text {
        font: 10px var(--ka-font-body);
        fill: var(--ka-parchment);
      }
      .lm {
        list-style: none;
        padding: 0;
        margin: 0;
        min-height: 0;
        overflow: auto;
      }
      .lm button {
        all: unset;
        box-sizing: border-box;
        width: 100%;
        min-height: 44px;
        padding: 0.4rem 0.6rem;
        cursor: pointer;
        border-bottom: 1px solid var(--ka-stone-700);
      }
      .lm button[aria-current='true'] {
        background: var(--ka-stone-700);
        color: var(--ka-gold);
      }
      .lm button:focus-visible {
        outline: 3px solid var(--ka-focus);
      }
      .notice {
        font-size: 0.9rem;
      }
    `,
  ];

  constructor() {
    super();
    this.query = '';
    this.viewer = 'idle';
    this.places = false;
    this.fs = 'off';
  }

  private readonly onFsChange = () => {
    const d = document as Document & { webkitFullscreenElement?: Element | null };
    const el = d.fullscreenElement ?? d.webkitFullscreenElement;
    clearTimeout(this.fsTimer);
    if (el) this.fs = 'native';
    else if (this.fs === 'native') this.fs = 'off';
  };
  private readonly onKey = (e: KeyboardEvent) => {
    if (e.key !== 'Escape') return;
    if (this.places) this.places = false;
    else if (this.fs === 'pseudo') this.exitFullscreen();
  };
  private fsTimer?: ReturnType<typeof setTimeout>;

  override connectedCallback() {
    super.connectedCallback();
    document.addEventListener('fullscreenchange', this.onFsChange);
    document.addEventListener('webkitfullscreenchange', this.onFsChange);
    document.addEventListener('keydown', this.onKey);
  }

  /** Must run inside the tap's own handler: browsers only allow full screen from a gesture. */
  private enterFullscreen() {
    const stage = this.renderRoot.querySelector<HTMLElement>('.stage') as
      (HTMLElement & { webkitRequestFullscreen?: () => void }) | null;
    if (!stage) return;
    const pseudo = () => {
      if (this.fs === 'off') this.fs = 'pseudo';
    };
    try {
      if (stage.requestFullscreen) stage.requestFullscreen().catch(pseudo);
      else if (stage.webkitRequestFullscreen) stage.webkitRequestFullscreen();
      else pseudo();
    } catch {
      pseudo();
    }
    // Some browsers (in-app web views) ignore the request without saying so.
    clearTimeout(this.fsTimer);
    this.fsTimer = setTimeout(pseudo, 1000);
  }

  private exitFullscreen() {
    clearTimeout(this.fsTimer);
    if (this.fs === 'pseudo') {
      this.fs = 'off';
      return;
    }
    const d = document as Document & { webkitExitFullscreen?: () => void };
    if (d.fullscreenElement) void d.exitFullscreen().catch(() => undefined);
    else d.webkitExitFullscreen?.();
  }

  private get choices(): MapChoice[] {
    return this.manifest.episodes
      .filter((e) => e.map)
      .sort((a, b) => b.number - a.number)
      .map((e) => ({
        label: `World after Episode ${e.number}`,
        episodeNumber: e.number,
        map: e.map!,
      }));
  }

  private get choice(): MapChoice | undefined {
    const all = this.choices;
    return all.find((c) => c.map.snapshotId === this.link.snapshot) ?? all[0];
  }

  private get selected(): PublicLandmark | undefined {
    return this.manifest.landmarks.find((l) => l.id === this.link.landmark);
  }

  private get dimension(): Dimension {
    const dims = this.choice?.map.dimensions ?? ['minecraft:overworld'];
    const want = this.link.dim ?? this.selected?.dimension;
    return want && dims.includes(want) ? want : dims[0]!;
  }

  private update_(patch: MapLink) {
    const next = { ...this.link, ...patch };
    navigate(`/map?${formatMapLink(next)}`, true);
  }

  private get autoLoad() {
    return matchMedia('(min-width: 800px)').matches;
  }

  private pendingAutoLoad = false;
  private navigatedTo = '';

  override willUpdate() {
    const key = this.choice?.map.snapshotId ?? '';
    if (key === this.loadedKey) return;
    this.adapter?.dispose();
    this.adapter = undefined;
    this.loadedKey = key;
    this.viewer = 'idle';
    this.pendingAutoLoad = !!key && this.autoLoad;
  }

  override updated() {
    // Our own full screen covers the window: stop the page behind it from scrolling.
    document.documentElement.style.overflow = this.fs === 'pseudo' ? 'hidden' : '';
    if (this.pendingAutoLoad) {
      this.pendingAutoLoad = false;
      void this.loadViewer();
    } else if (this.adapter && this.selected && this.selected.id !== this.navigatedTo) {
      const l = this.selected;
      this.navigatedTo = l.id;
      this.adapter.navigate(this.viewOf(l));
      this.sendLandmarks();
    }
  }

  /** Landmarks of the snapshot's world, shown as tappable pins inside the 3D map. */
  private sendLandmarks() {
    this.adapter?.setLandmarks(this.worldLandmarks, this.selected?.id);
  }

  /** Landmarks of the chosen snapshot's world, in the shape the viewer wants. */
  private get worldLandmarks() {
    const worldId = this.choice?.map.worldId;
    return this.manifest.landmarks
      .filter((l) => l.worldId === worldId)
      .map((l) => ({ id: l.id, name: l.name, dimension: l.dimension, x: l.x, y: l.y, z: l.z }));
  }

  private async loadViewer() {
    const c = this.choice;
    if (!c || this.viewer === 'loading') return;
    const { BlueMapIframeAdapter, hasWebGL, startCamera, fliesHere, viewOf } =
      await import('@ka/map-adapter');
    this.viewOf = (l) => viewOf(l, fliesHere());
    if (!hasWebGL()) {
      this.viewer = 'unsupported';
      return;
    }
    this.viewer = 'loading';
    await this.updateComplete;
    const host = this.renderRoot.querySelector<HTMLElement>('.viewer');
    if (!host) return;
    const adapter = new BlueMapIframeAdapter(c.map.baseUrl, c.map.entry, `3D map: ${c.label}`);
    adapter.onMarkerSelected((id) => this.update_({ landmark: id }));
    this.adapter = adapter;
    this.sendLandmarks(); // queued until the viewer reports ready
    this.navigatedTo = this.selected?.id ?? '';
    try {
      // Start close to a landmark: from BlueMap's default (1,500 blocks up) it loads no detail.
      await adapter.load(
        host,
        startCamera(this.worldLandmarks, this.dimension, this.selected?.id, fliesHere()),
      );
      this.viewer = 'ready';
    } catch {
      this.viewer = 'failed';
    }
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('fullscreenchange', this.onFsChange);
    document.removeEventListener('webkitfullscreenchange', this.onFsChange);
    document.removeEventListener('keydown', this.onKey);
    clearTimeout(this.fsTimer);
    document.documentElement.style.overflow = '';
    this.adapter?.dispose();
    this.adapter = undefined;
    this.loadedKey = '';
  }

  /** Static top-down plot of landmark positions: works without WebGL or a render. */
  private overview(landmarks: PublicLandmark[]) {
    const pts = landmarks.filter((l) => l.dimension === this.dimension);
    if (!pts.length)
      return html`<p class="overlay muted">
        No landmarks in the ${DIMENSION_LABELS[this.dimension]} yet.
      </p>`;
    const xs = pts.map((p) => p.x);
    const zs = pts.map((p) => p.z);
    const pad = 40;
    const [minX, maxX, minZ, maxZ] = [
      Math.min(...xs) - pad,
      Math.max(...xs) + pad,
      Math.min(...zs) - pad,
      Math.max(...zs) + pad,
    ];
    const w = 400;
    const h = 300;
    const sx = (x: number) => ((x - minX) / Math.max(1, maxX - minX)) * (w - 40) + 20;
    const sz = (z: number) => ((z - minZ) / Math.max(1, maxZ - minZ)) * (h - 40) + 20;
    return html`<svg
      class="overview"
      viewBox="0 0 ${w} ${h}"
      role="img"
      aria-label="Overview of landmark positions in the ${DIMENSION_LABELS[this.dimension]}"
    >
      ${pts.map(
        (p) => svg`<g>
          <rect x=${sx(p.x) - 5} y=${sz(p.z) - 5} width="10" height="10" fill=${p.id === this.link.landmark ? 'var(--ka-gold)' : 'var(--ka-red)'} stroke="#000"/>
          <text x=${sx(p.x) > w / 2 ? sx(p.x) - 8 : sx(p.x) + 8} y=${sz(p.z) + 4} text-anchor=${sx(p.x) > w / 2 ? 'end' : 'start'}>${p.name}</text>
        </g>`,
      )}
    </svg>`;
  }

  private stage() {
    const c = this.choice;
    const ls = this.manifest.landmarks;
    if (!c) {
      return html`<div class="stage">
          ${this.overview(ls)}
          <div class="tools">${this.placesToggle()}</div>
          ${this.places ? this.placesPanel() : nothing}
        </div>
        <p class="notice muted">
          No map snapshot has been published yet. Showing landmark positions only.
        </p>`;
    }
    const overlay =
      this.viewer === 'idle'
        ? html`<div class="overlay">
            ${this.overview(ls)}
            <button
              class="btn green"
              @click=${() => {
                if (!this.autoLoad) this.enterFullscreen(); // still inside the tap
                void this.loadViewer();
              }}
            >
              Open 3D map${this.autoLoad ? '' : ' (full screen)'}
            </button>
          </div>`
        : this.viewer === 'loading'
          ? html`<p class="overlay">Loading the world…</p>`
          : this.viewer === 'unsupported' || this.viewer === 'failed'
            ? html`<div class="overlay">
                ${this.overview(ls)}
                <p>
                  ${this.viewer === 'failed' ? 'The 3D map could not load.' : 'Your browser cannot show the 3D map.'}
                  Use the landmark list instead.
                </p>
              </div>`
            : nothing;
    const full = this.fs !== 'off';
    return html`<div class="stage ${this.fs === 'pseudo' ? 'pseudo' : ''}">
        <div class="viewer" ?hidden=${this.viewer !== 'ready' && this.viewer !== 'loading'}></div>
        ${overlay}
        <div class="tools">
          ${this.placesToggle()}
          ${
            this.viewer === 'ready' || full
              ? html`<button
                  class="btn"
                  @click=${() => (full ? this.exitFullscreen() : this.enterFullscreen())}
                >
                  ${full ? 'Exit full screen' : 'Full screen'}
                </button>`
              : nothing
          }
        </div>
        ${this.places ? this.placesPanel() : nothing}
      </div>
      <p class="notice muted">
        Saved snapshot — not live. ${c.label} · captured ${fmtDate(c.map.capturedAt)}
        ${this.manifest.publishedAt ? html`· published ${fmtDate(this.manifest.publishedAt)}` : nothing}
      </p>`;
  }

  private placesToggle() {
    return html`<button
      class="btn places-toggle"
      aria-expanded=${this.places}
      aria-controls="places"
      @click=${() => (this.places = !this.places)}
    >
      ${this.places ? 'Close places' : 'Places'}
    </button>`;
  }

  /** The landmark list, drawn over the map so it also works in full screen. */
  private placesPanel() {
    const q = this.query.toLowerCase();
    const sel = this.selected;
    const list = this.manifest.landmarks.filter(
      (l) => l.dimension === this.dimension && (!q || l.name.toLowerCase().includes(q)),
    );
    return html`<section class="panel places" id="places" aria-labelledby="lm-h">
      <h2 id="lm-h">Places</h2>
      <label
        >Search
        <input
          type="search"
          .value=${this.query}
          @input=${(e: Event) => (this.query = (e.target as HTMLInputElement).value)}
      /></label>
      ${
        list.length
          ? html`<ul class="lm">
              ${list.map(
                (l) =>
                  html`<li>
                    <button
                      aria-current=${l.id === sel?.id ? 'true' : 'false'}
                      @click=${() => {
                        this.places = false;
                        this.update_({ landmark: l.id, dim: l.dimension });
                        void this.updateComplete.then(() =>
                          this.renderRoot.querySelector<HTMLElement>('.places-toggle')?.focus(),
                        );
                      }}
                    >
                      ${l.name} <span class="muted">(${l.x}, ${l.y}, ${l.z})</span>
                    </button>
                  </li>`,
              )}
            </ul>`
          : html`<p class="muted">No places match.</p>`
      }
    </section>`;
  }

  override render() {
    const c = this.choice;
    const dims = c?.map.dimensions ?? [...new Set(this.manifest.landmarks.map((l) => l.dimension))];
    const sel = this.selected;
    const epBySlug = new Map(this.manifest.episodes.map((e) => [e.id, e]));
    return html`
      <h1>Explore the World</h1>
      <div class="bar">
        ${
          this.choices.length > 1
            ? html`<label
                >Snapshot
                <select
                  @change=${(e: Event) => this.update_({ snapshot: (e.target as HTMLSelectElement).value as MapLink['snapshot'] })}
                >
                  ${this.choices.map((x) => html`<option value=${x.map.snapshotId} ?selected=${x === c}>${x.label}</option>`)}
                </select></label
              >`
            : nothing
        }
        ${
          dims.length > 1
            ? html`<label
                >Dimension
                <select
                  @change=${(e: Event) => this.update_({ dim: (e.target as HTMLSelectElement).value as Dimension, landmark: undefined })}
                >
                  ${dims.map((d) => html`<option value=${d} ?selected=${d === this.dimension}>${DIMENSION_LABELS[d]}</option>`)}
                </select></label
              >`
            : nothing
        }
      </div>
      <div class="layout">
        <div>${this.stage()}</div>
        <div>
          ${
            sel
              ? html`<section class="panel" aria-live="polite">
                  <h2>${sel.name}</h2>
                  <p class="muted">
                    ${DIMENSION_LABELS[sel.dimension]} · x ${sel.x}, y ${sel.y}, z
                    ${sel.z}${sel.underground ? ' · entrance only' : ''}
                  </p>
                  <ka-markdown .source=${sel.description}></ka-markdown>
                  ${sel.episodeLinks.map((x) => {
                    const ep = epBySlug.get(x.episodeId);
                    return ep
                      ? html`<p>
                          <a href="/episodes/${ep.slug}">Episode ${ep.number}: ${ep.title}</a
                          >${x.t !== null ? ` at ${fmtTime(x.t)}` : ''}
                        </p>`
                      : nothing;
                  })}
                  ${this.manifest.lore
                    .filter((l) => l.landmarkIds.includes(sel.id))
                    .map((l) => html`<p>Lore: <a href="/lore/${l.slug}">${l.title}</a></p>`)}
                  <p>
                    <button
                      class="btn"
                      @click=${() => navigator.clipboard?.writeText(location.href)}
                    >
                      Copy link to this place
                    </button>
                  </p>
                </section>`
              : nothing
          }
        </div>
      </div>
    `;
  }
}
customElements.define('ka-map-view', KaMapView);
